import { z } from 'zod';
import { ERROR_CODES } from '../constants';

/**
 * Request schemas (Zod) are used for runtime validation on the server and reused for form
 * validation on the client. Response shapes are plain TypeScript interfaces: the server
 * produces them from typed queries, so a second runtime validation pass on the way out
 * would cost latency without catching anything the type system doesn't.
 */

/** Every successful response carries the server's clock so clients never trust the device. */
export interface Envelope<T> {
  data: T;
  serverTime: string;
}

export interface ApiErrorBody {
  error: {
    code: keyof typeof ERROR_CODES;
    message: string;
    details?: unknown;
  };
  serverTime: string;
}

export const uuidParam = z.string().uuid();

export const idParams = z.object({ id: z.string().uuid() });
export const weekIdParams = z.object({ weekId: z.string().uuid() });
export const seasonIdParams = z.object({ seasonId: z.string().uuid() });
export const leagueIdParams = z.object({ leagueId: z.string().uuid() });

export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuery>;

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

/**
 * Spreads are half-point multiples in a sane range. Rejecting a 40-point line is a
 * cheap guard against a commissioner fat-fingering `45` instead of `4.5`.
 */
export const spreadValue = z
  .number()
  .min(-60)
  .max(60)
  .refine((v) => Math.abs(v * 2 - Math.round(v * 2)) < 1e-9, {
    message: 'Spread must be a multiple of 0.5',
  });

/**
 * A line a contest can actually be graded on: a half point, never a whole number.
 *
 * Whole numbers are rejected rather than quietly corrected. Selection applies the hook
 * automatically, so the only way to reach this validator with a `-7` is a commissioner
 * typing it by hand — and silently saving `-7.5` when they typed `-7` would mean the line
 * on screen is not the line they entered.
 */
export const decisiveSpreadValue = spreadValue.refine(
  (v) => Math.abs(Math.round(v * 2)) % 2 === 1,
  { message: 'Use a half point (-7.5, not -7) — this league does not allow pushes' },
);

export const isoDateTime = z.string().datetime({ offset: true });

export const teamScore = z.number().int().min(0).max(200);
